"use client";

import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { importSourceInitialState, useImport } from "@/context/importContext";
import { useToast } from "@/context/ToastContext";
import { useSession } from "@/context/SessionContext";
import { useImportStatic } from "@/utils/hooks/import/importHooks";
import {
  getAvailableProfiles,
  getRealStaticExamples,
  ImportProfileType,
} from "@/utils/import/profiles/importProfiles";
import {
  buildTemplateWorkbook,
  columnDescription,
  toDelimitedText,
} from "@/utils/import/generic-data-processing";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { columnTypeErrors } from "@/utils/import/validations/columnValidations";
import { Download, FileText } from "lucide-react";
import React, { useState } from "react";

type TemplateFormatType = "xlsx" | "csv";

// Both formats need the file saved with the browser: one link per download.
const saveBlob = (blob: Blob, fileName: string) => {
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = fileName;
  a.click();
  window.URL.revokeObjectURL(url);
};

const ProfileSelector = React.memo(() => {
  const { session } = useSession();
  const { languageCode } = session;
  const { setImportData } = useImport();
  const { showToast } = useToast();
  const [templateFormat, setTemplateFormat] = useState<TemplateFormatType>("xlsx");

  // Obtener datos estáticos para usar ejemplos reales
  const staticData = useImportStatic(languageCode);
  const realExamples = getRealStaticExamples(staticData, true); // true = isImportContext

  // Obtener perfiles disponibles con datos dinámicos
  const profiles = getAvailableProfiles(staticData);

  const downloadTemplate = async (profile: ImportProfileType) => {
    const headers = Object.keys(profile.columns);
    const sampleRows: string[][] = [];

    // Add required fields row with descriptions
    const requiredRow = headers.map((header) => {
      const isRequired =
        languageCode === "en"
          ? profile.columns[header].required
            ? "Required"
            : "Optional"
          : profile.columns[header].required
          ? "Requerido"
          : "Opcional";
      const columnType = profile.columns[header].type;

      const errorMessage = columnTypeErrors[columnType]?.[languageCode];

      const error = errorMessage ? `- ${errorMessage}` : "";

      const customErrorMessage =
        profile.columns[header].customError?.[languageCode];
      const customError = customErrorMessage ? `- ${customErrorMessage}` : "";

      return `${isRequired} ${error} ${customError}`;
    });

    // Get the maximum number of sample data entries
    const maxSamples = Math.max(
      ...Object.values(profile.columns).map((col) => {
        const samples =
          col.sampleData?.[languageCode] || col.sampleData?.["en"] || [];
        return samples.length;
      })
    );

    // Create sample rows with real data for specific fields
    for (let i = 0; i < Math.min(maxSamples, 3); i++) {
      const row = headers.map((header) => {
        const column = profile.columns[header];
        const sampleData = column.sampleData;

        // Usar datos reales para campos específicos de contratos
        if (header === "contract_rate_period") {
          return realExamples.ratePeriodExamples[i] || "";
        }
        if (header === "contract_invoice_frequency") {
          return realExamples.invoiceFrequencyExamples[i] || "";
        }
        if (header === "contract_invoice_time_mode") {
          return realExamples.invoiceTimeModeExamples[i] || "";
        }
        if (header === "contract_invoice_mode") {
          return realExamples.invoiceModeExamples[i] || "";
        }
        if (header.includes("gender_id")) {
          return realExamples.genderExamples[i] || "";
        }

        // Para otros campos, usar los ejemplos del perfil
        const samples = sampleData?.[languageCode] || sampleData?.["en"] || [];
        return samples[i] !== undefined ? samples[i] : "";
      });
      sampleRows.push(row.map(String));
    }

    // Three instruction rows (header, info, samples), each with its label in an
    // extra first column. The wizard leaves them out when the sheet is pasted back.
    const templateRows: string[][] = [
      [
        columnDescription.header[languageCode],
        ...headers.map(
          (header) =>
            profile.columns[header].fieldHeader?.[languageCode] ||
            profile.columns[header].fieldHeader?.en ||
            header
        ),
      ],
      [columnDescription.info[languageCode], ...requiredRow],
      ...sampleRows.map((row) => [
        columnDescription.sampleData[languageCode],
        ...row,
      ]),
    ];

    if (templateFormat === "xlsx") {
      try {
        // Columns of numbers, shifted by the label column of the instruction rows.
        const numberColumns = headers
          .map((header, index) => (profile.columns[header].type === "number" ? index + 1 : -1))
          .filter((index) => index > 0);
        const blob = await buildTemplateWorkbook(templateRows, {
          sheetName: profile.name[languageCode] || profile.id,
          numberColumns,
        });
        saveBlob(blob, `${profile.id}-template.xlsx`);
      } catch (error) {
        console.error("Error building the Excel template:", error);
        showToast({
          description: "No se pudo generar el archivo de Excel. Prueba con CSV.",
          variant: "destructive",
        });
      }
      return;
    }

    // Spanish Excel opens a CSV by double click only when it is separated by
    // semicolons; the byte order mark makes it read the accents as UTF-8.
    const separator = languageCode === "en" ? "," : ";";
    const csvContent = "﻿" + toDelimitedText(templateRows, separator);
    saveBlob(new Blob([csvContent], { type: "text/csv" }), `${profile.id}-template.csv`);
  };

  return (
    <div className="space-y-4">
      {/* <div>
        <h2 className="text-xl font-semibold text-fg mb-2">
          Seleccionar Perfil de Importación
        </h2>
        <p className="text-gr-500 text-sm">
          Elige el tipo de datos que deseas importar
        </p>
      </div> */}

      <p className="text-sm text-fg-muted">
        Importa primero los candidatos y después los contratos. Cada contrato trae su
        horario semanal; los empleados de cada turno se asignan después en la plataforma.
      </p>

      <div className="flex items-center gap-3">
        <Label htmlFor="template-format" className="text-sm">
          Formato de las plantillas
        </Label>
        <div className="w-44">
          <Select
            value={templateFormat}
            onValueChange={(value) => setTemplateFormat(value as TemplateFormatType)}
          >
            <SelectTrigger id="template-format">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="xlsx">Excel (.xlsx)</SelectItem>
              <SelectItem value="csv">CSV</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="grid gap-4">
        {profiles.map((profile, profileIndex) => (
          <Card
            key={profile.id}
            className={`flex items-center  justify-between w-full transition-all hover:shadow-md`}
          >
            <CardHeader className="pb-3">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2">
                  <FileText className="h-5 w-5 text-ac" />
                  <div>
                    <CardTitle className="text-lg">
                      {profileIndex + 1}. {profile.name[languageCode]}
                    </CardTitle>
                    <CardDescription className="text-fg-muted mt-1">
                      {profile.description[languageCode]}
                    </CardDescription>
                  </div>
                </div>
              </div>
            </CardHeader>

            <CardContent className="flex items-center justify-between gap-4 p-2">
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => downloadTemplate(profile)}
                >
                  <Download className="h-4 w-4 mr-2" />
                  Descargar Plantilla
                </Button>
              </div>

              <Button
                variant="outline"
                onClick={(e) => {
                  e.preventDefault();
                  setImportData((prev) => ({
                    ...prev,
                    selectedProfile: profile,
                    currentStep: "upload",
                    importType: profile.id,
                    // Data of another profile is not carried over.
                    source: importSourceInitialState,
                    fileErrors: [],
                  }));
                }}
              >
                Importar
              </Button>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
});

ProfileSelector.displayName = "ProfileSelector";

export default ProfileSelector;
